import { useRef, useContext } from 'react'
import { motion } from 'framer-motion'
import { useInView } from '../hooks/useInView'
import Footer from '../landing/components/Footer'
import { RouteTransitionContext } from '../components/AnimatedRoutes'
import ctaIcon from '../assets/cta-02.svg'

/* ── Animation helpers ────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

function Section({
  children,
  className = '',
  immediate = false,
}: {
  children: React.ReactNode
  className?: string
  immediate?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { threshold: 0.15 })
  const isTransitioning = useContext(RouteTransitionContext)

  const shouldShow = immediate ? !isTransitioning : !isTransitioning && isInView

  return (
    <motion.div
      ref={ref}
      variants={stagger}
      initial="hidden"
      animate={shouldShow ? 'visible' : 'hidden'}
      className={className}
    >
      {children}
    </motion.div>
  )
}

/* ── Insight feature graphics ─────────────────────── */
const ValueGraphic = () => (
  <div className="absolute inset-4 flex flex-col justify-between rounded-xl border border-oak-black/[0.06] bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
    <div className="flex items-center justify-between">
      <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-oak-black/50">
        Fair market value
      </span>
      <span className="rounded-full bg-oak-green/10 px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wider text-oak-green">
        Live
      </span>
    </div>
    <div>
      <div className="text-[28px] font-semibold leading-none tracking-[-0.8px] text-oak-black">
        $2,480
      </div>
      <div className="mt-0.5 text-[9.5px] font-medium text-oak-black/40">
        Estimated fair value
      </div>
    </div>
    <div>
      <div className="relative h-1.5 rounded-full bg-oak-black/[0.06]">
        <div className="absolute inset-y-0 left-[14%] right-[18%] rounded-full bg-oak-yellow" />
        <div className="absolute top-1/2 left-[58%] h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-oak-black shadow-sm" />
      </div>
      <div className="mt-1.5 flex justify-between text-[9px] font-medium text-oak-black/40">
        <span>$1,820</span>
        <span>$2,940</span>
      </div>
    </div>
  </div>
)

const DemandGraphic = () => {
  const rows = [
    { name: 'Alpine 2P Tent', delta: '+24%', hl: true },
    { name: 'Trail Ridge Pack', delta: '+16%', hl: false },
    { name: 'Campfire Mug Set', delta: '+9%', hl: false },
  ]
  return (
    <div className="absolute inset-4 rounded-xl border border-oak-black/[0.06] bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-oak-black/50">
          Trending
        </span>
        <span className="text-[9px] font-medium text-oak-black/40">Last 24h</span>
      </div>
      <div className="mt-2.5 space-y-1">
        {rows.map((r) => (
          <div
            key={r.name}
            className={`flex items-center justify-between rounded-lg px-2 py-1.5 ${
              r.hl ? 'bg-oak-yellow/50' : ''
            }`}
          >
            <span className="text-[11px] font-medium tracking-[-0.2px] text-oak-black">
              {r.name}
            </span>
            <span className="text-[11px] font-semibold text-oak-green">
              {r.delta}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

const ActivityGraphic = () => (
  <div className="absolute inset-4 flex flex-col overflow-hidden rounded-xl border border-oak-black/[0.06] bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-oak-green opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-oak-green" />
        </span>
        <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-oak-black/50">
          Live activity
        </span>
      </div>
      <span className="text-[9px] font-medium text-oak-black/40">+38%</span>
    </div>

    <svg viewBox="0 0 220 60" fill="none" className="mt-2 w-full flex-1">
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD72C" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FFD72C" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M 0 42 C 20 42 28 20 50 24 S 90 58 112 40 S 150 8 172 18 S 200 40 220 30 L 220 60 L 0 60 Z"
        fill="url(#sparkFill)"
      />
      <path
        d="M 0 42 C 20 42 28 20 50 24 S 90 58 112 40 S 150 8 172 18 S 200 40 220 30"
        stroke="#FFD72C"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="220" cy="30" r="8" fill="#59B744" opacity="0.2" />
      <circle cx="220" cy="30" r="3.5" fill="#59B744" />
    </svg>

    <div className="mt-1.5 flex justify-between text-[8.5px] font-medium text-oak-black/40">
      <span>Mon</span>
      <span>Wed</span>
      <span>Fri</span>
      <span>Sun</span>
    </div>
  </div>
)

const SpeedGraphic = () => (
  <div className="absolute inset-4 rounded-xl border border-oak-black/[0.06] bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
    <div className="flex items-center justify-between">
      <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-oak-black/50">
        Suggested price
      </span>
    </div>
    <div className="mt-2 text-[28px] font-semibold leading-none tracking-[-0.8px] text-oak-black">
      $1,680
    </div>

    <div className="mt-3 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="w-[60px] text-[9px] font-medium tracking-[0.04em] text-oak-black/40">
          You
        </span>
        <div className="h-1.5 flex-1 rounded-full bg-oak-black/[0.06]">
          <div className="h-full w-[84%] rounded-full bg-oak-black" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-[60px] text-[9px] font-medium tracking-[0.04em] text-oak-black/40">
          Market avg
        </span>
        <div className="h-1.5 flex-1 rounded-full bg-oak-black/[0.06]">
          <div className="h-full w-[68%] rounded-full bg-oak-black/25" />
        </div>
      </div>
    </div>

    <div className="mt-3 flex items-center gap-1 text-[10px] font-semibold text-oak-green">
      <svg width="9" height="9" viewBox="0 0 10 10" fill="none" className="shrink-0">
        <path
          d="M5 9V1M1 5l4-4 4 4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      16% above market average
    </div>
  </div>
)

const insightFeatures = [
  {
    title: 'Fair market value, instantly',
    copy: 'Real-time valuation surfaces the right price the moment a listing goes live — no guesswork, no stale comps.',
    Graphic: ValueGraphic,
  },
  {
    title: 'See what\u2019s in demand',
    copy: 'Spot what your community is buying and selling before the trend is obvious to anyone else.',
    Graphic: DemandGraphic,
  },
  {
    title: 'Rich, consistent context',
    copy: 'Every product carries the same accurate, up-to-date context everywhere it appears \u2014 automatically.',
    Graphic: ActivityGraphic,
  },
  {
    title: 'Signals that matter to you',
    copy: 'Cortex tracks the metrics each licensor cares about and feeds them back, so your community stays one step ahead.',
    Graphic: SpeedGraphic,
  },
]


/* ── Page ─────────────────────────────────────────── */
export default function CortexPage() {
  return (
    <div className="min-h-screen bg-oak-cream">
      {/* HERO */}
      <section className="rounded-section bg-oak-cream pb-16 pt-28 md:pb-24 md:pt-36">
        <Section className="mx-auto max-w-nav px-6" immediate>
          <motion.span
            variants={fadeUp}
            className="text-sm font-semibold uppercase tracking-widest text-gold-500"
          >
            Cortex
          </motion.span>
          <motion.h1
            variants={fadeUp}
            className="mt-4 font-serif text-[clamp(48px,8vw,120px)] leading-[0.85] tracking-[-2.88px] text-oak-black"
          >
            Smarter products
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-[700px] text-[clamp(16px,2vw,22px)] font-medium leading-[1.3] tracking-[-0.2px] text-oak-black/70 md:mt-8"
          >
            Cortex is the engine that enriches every indexed item with real
            context and tracks the signals that matter to you — fed back
            to you in real time so you can grow faster.
          </motion.p>
        </Section>
      </section>

      {/* INSIGHT */}
      <section className="rounded-section bg-oak-black px-6 py-20 md:py-32">
        <Section className="mx-auto max-w-nav px-6" immediate>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-[14vw] leading-[0.95] tracking-[-1.5px] md:text-[clamp(36px,5vw,64px)] text-white"
          >
            Context around every product
          </motion.h2>
          <motion.div variants={fadeUp} className="mt-8 max-w-[640px] space-y-5">
            <p className="text-[clamp(16px,1.8vw,20px)] font-medium leading-[1.5] tracking-[-0.2px] text-white/70">
              Cortex gathers the data points around each product — who&apos;s
              talking about it, what&apos;s trending, where it&apos;s valued — and
              turns them into rich, dynamic context that travels with your
              products everywhere they go.
            </p>
            <p className="text-[clamp(16px,1.8vw,20px)] font-medium leading-[1.5] tracking-[-0.2px] text-white/70">
              Then it tracks the signals that matter to you and feeds
              them back, so you always know what&apos;s moving and where to grow.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-5 md:mt-20 md:grid-cols-2 md:gap-6">
            {insightFeatures.map(({ title, copy, Graphic }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                className="relative overflow-hidden rounded-3xl bg-white p-5 shadow-[0_4px_16px_rgba(0,0,0,0.06)] md:p-6"
              >
                <div className="relative h-44 overflow-hidden rounded-2xl bg-oak-cream md:h-48">
                  <Graphic />
                </div>
                <h3 className="mt-6 text-[clamp(20px,2.2vw,26px)] font-semibold tracking-[-0.4px] text-oak-black">
                  {title}
                </h3>
                <p className="mt-2 text-[15px] leading-[1.55] tracking-[-0.2px] text-oak-black/60">
                  {copy}
                </p>
              </motion.div>
            ))}
          </div>
        </Section>
      </section>

      {/* HOW IT CONNECTS TO OAK */}
      <section className="rounded-section bg-white px-6 py-20 md:py-32">
        <Section className="mx-auto max-w-nav">
          <motion.h2
            variants={fadeUp}
            className="font-serif text-[14vw] leading-[0.95] tracking-[-1.5px] md:text-[clamp(36px,5vw,64px)] text-oak-black"
          >
            Flexible by design
          </motion.h2>
          <motion.div variants={fadeUp} className="mt-8 max-w-[640px] space-y-5">
            <p className="text-[clamp(16px,1.8vw,20px)] font-medium leading-[1.5] tracking-[-0.2px] text-oak-black/70">
              Cortex powers Oak&apos;s own apps like Nanza, or runs as a
              standalone engine on other third party platforms.
            </p>
            <p className="text-[clamp(16px,1.8vw,20px)] font-medium leading-[1.5] tracking-[-0.2px] text-oak-black/70">
              As your community grows, the context and signals grow with it.
            </p>
          </motion.div>
        </Section>
      </section>

      {/* FINAL CTA */}
      <section className="rounded-section bg-oak-butter py-24 md:py-40">
        <Section className="mx-auto flex max-w-nav flex-col items-center px-6 text-center">
          <motion.h2
            variants={fadeUp}
            className="font-serif text-[14vw] leading-[0.85] tracking-[-2.88px] text-oak-black md:text-[clamp(48px,8vw,120px)]"
          >
            Build smarter
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-[570px] text-[clamp(16px,2vw,26px)] font-medium leading-[1.19] tracking-[-0.26px] text-oak-black md:mt-12"
          >
            Cortex unlocks deeper intelligence as your community grows.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 md:mt-16">
            <a
              href="#demo"
              className="inline-flex items-center gap-3 rounded-pill bg-oak-black px-6 py-3.5 text-[clamp(16px,1.5vw,21px)] font-medium tracking-[-0.63px] text-white transition-all hover:opacity-80"
            >
              Request a Demo
              <img src={ctaIcon} alt="" className="h-8 w-8" />
            </a>
          </motion.div>
        </Section>
      </section>

      <Footer />
    </div>
  )
}
